<template>
    <div id="events-block">
        <div
            v-for="(event, index) in events"
            :key="`${event.id}-${event.date}-${index}`"
            :class="[
                'event-block',
                verifyIfOutDated(event.date, event.startTime, event.endTime, event.allDay)
                    ? '--is-outdated'
                    : ''
            ]"
        >
            <div class="day-block">
                <div class="week-day">
                    {{ $t(`CalendarsPage.WeekDays.${getDayOfWeek(event.date)}`) }}
                </div>
                <div class="day">{{ getDayOfMonth(event.date) }}</div>
            </div>
            <div class="event-info">
                <div class="event-title">{{ event.name }}</div>
                <div class="info-block">
                    <div class="first-column">
                        <div class="event-description">
                            <EventIcon class="event-icon" /><span class="text">
                                {{ getEventType(event) }}
                            </span>
                        </div>
                        <div class="event-duration">
                            <DurationIcon class="duration-icon" />
                            <span class="text">
                                {{ getDurationEvent(event) }}
                            </span>
                        </div>
                    </div>
                    <div class="second-column">
                        <div
                            v-if="event.resourceIds.length > 0"
                            class="event-resource"
                        >
                            <ResourceIcon class="resource-icon" />
                            <span class="text">{{
                                `${event.resourceIds.length} ${$t("CalendarsPage.resource")}${event.resourceIds.length > 1 ? "s" : ""}`
                            }}</span>
                        </div>
                        <div
                            v-if="event.isRecurrent && windowWidth > 600"
                            class="event-recurrence"
                        >
                            <RecurrentIcon class="recurrent-icon" />
                            <span class="text">
                                {{ $t("CalendarsPage.Recurrence") }}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
            <div
                v-show="windowWidth > 600"
                class="action-block"
            >
                <sys-button
                    :id="`${event.id}-${event.date}-${index}`"
                    scheme="square"
                    variant="outline square-danger trash-icon-md"
                    @click="
                        () => {
                            getEvent(event);
                        }
                    "
                >
                    <DeleteIcon />
                </sys-button>
                <sys-button
                    size="md"
                    scheme="square"
                    variant="outline"
                    @click="
                        () => {
                            exceptionPermissions.find((e) => e.type === 'update') &&
                                handleEditEvent(event.id);
                        }
                    "
                    class="white-background"
                >
                    {{ $t("Edit") }}
                </sys-button>
                <Popover
                    v-if="exceptionPermissions.find((e) => e.type === 'delete')"
                    :items="handleDesktopOptionList(event.isRecurrent)"
                    :target="`${event.id}-${event.date}-${index}`"
                    placement="bottomright"
                    custom-position="--max-content"
                    triggers="focus"
                />
            </div>
            <div
                v-show="windowWidth <= 600"
                class="control-block"
            >
                <OptionIcon
                    class="option-svg"
                    @click="getEvent(event)"
                    :id="`event-${event.id}-${event.date}-${index}`"
                    tabindex="0"
                />
                <RecurrentIcon
                    v-if="event.isRecurrent"
                    class="recurrent-icon"
                />
                <Popover
                    :items="handleMobileOptionList(event.isRecurrent)"
                    :target="`event-${event.id}-${event.date}-${index}`"
                    placement="bottomright"
                    custom-position="--max-content"
                    triggers="focus"
                />
            </div>
        </div>
    </div>
</template>

<script>
    import DurationIcon from "@core/assets/icons/duration-icon.svg";
    import EventIcon from "@core/assets/icons/event-icon.svg";
    import OptionIcon from "@core/assets/icons/option-icon.svg";
    import EditIcon from "@core/assets/icons/edit-icon.svg";
    import DeleteIcon from "@core/assets/icons/delete-trash-icon.svg";
    import Popover from "@core/layouts/layout-horizontal/components/Menu/Popover.vue";
    import PopoverDeleteIcon from "@core/assets/icons/delete-icon.svg";
    import RecurrentIcon from "@core/assets/icons/recurrent-icon.svg";
    import ResourceIcon from "@core/assets/icons/select-resource-icon.svg";
    import SysButton from "@/@core/components/sys-button";
    import { mapMutations, mapState } from "vuex";
    import exceptionEventTypes from "@/views/pages/calendar/enums/exceptionEventTypes";
    import { calendarInitialState } from "@/store/calendar";

    export default {
        components: {
            DurationIcon,
            EventIcon,
            OptionIcon,
            EditIcon,
            RecurrentIcon,
            ResourceIcon,
            DeleteIcon,
            Popover,
            PopoverDeleteIcon,
            SysButton
        },
        props: {
            events: {
                type: Array,
                default: () => []
            }
        },
        data() {
            return {
                currentEvent: null,
                optionList: [
                    {
                        id: 1,
                        title: "CalendarsPage.DeleteEvent",
                        icon: PopoverDeleteIcon,
                        click: () => {
                            this.exceptionPermissions.find((e) => e.type === "delete") &&
                                this.deleteEvent(this.currentEvent?.id);
                        }
                    },
                    {
                        id: 2,
                        title: "CalendarsPage.DeleteRecurrence",
                        icon: PopoverDeleteIcon,
                        click: () => {
                            this.exceptionPermissions.find((e) => e.type === "delete") &&
                                this.deleteRecurrenceEvent(this.currentEvent);
                        }
                    }
                ],
                mobileOptionList: [
                    {
                        id: 1,
                        title: "ResourcePage.Edit",
                        icon: EditIcon,
                        click: () => {
                            if (this.exceptionPermissions.find((e) => e.type === "update")) {
                                this.UPDATE_MODAL_EVENT_ID(this.currentEvent.id);
                                this.UPDATE_MODAL_EVENTS_TOGGLE(true);
                            }
                        }
                    },
                    {
                        id: 2,
                        title: "CalendarsPage.DeleteEvent",
                        icon: PopoverDeleteIcon,
                        click: () => {
                            this.exceptionPermissions.find((e) => e.type === "delete") &&
                                this.deleteEvent(this.currentEvent?.id);
                        }
                    },
                    {
                        id: 3,
                        title: "CalendarsPage.DeleteRecurrence",
                        icon: PopoverDeleteIcon,
                        click: () => {
                            this.exceptionPermissions.find((e) => e.type === "delete") &&
                                this.deleteRecurrenceEvent(this.currentEvent);
                        }
                    }
                ]
            };
        },
        methods: {
            ...mapMutations("app", ["UPDATE_FLAG_SHOW_OVERLAY"]),
            ...mapMutations("calendar", [
                "UPDATE_MONTH_GROUPS_EXCEPTION",
                "UPDATE_MONTH_GROUPS_WITH_RECURRENCY_EXCEPTION",
                "UPDATE_MODAL_EVENTS_TOGGLE",
                "UPDATE_MODAL_EVENT_ID"
            ]),
            verifyIfOutDated(dateString, startTime, endTime, allDay) {
                const inputDate = new Date(dateString);

                if (allDay) {
                    inputDate?.setHours?.(23, 59, 59, 999);
                } else {
                    const [endHour, endMinute] = endTime.split(":");
                    inputDate?.setHours?.(endHour ?? 0, endMinute ?? 0, 0, 0);
                }

                const today = new Date();

                return inputDate < today;
            },
            getDayOfWeek(dateString) {
                const date = new Date(dateString);
                const daysOfWeek = [
                    "sunday",
                    "monday",
                    "tuesday",
                    "wednesday",
                    "thursday",
                    "friday",
                    "saturday"
                ];

                return daysOfWeek[date.getDay()];
            },
            getDay(dateString) {
                const date = new Date(dateString);
                return date.getDay();
            },
            getDayOfMonth(dateString) {
                const date = new Date(dateString);
                return date.getDate();
            },
            getEventType(event) {
                const eventType = this.context.calendarEventTypes.find(
                    (e) => e.id == event.eventTypeId
                );

                if (
                    event.eventTypeId != exceptionEventTypes.overtime &&
                    event.eventTypeId != exceptionEventTypes.onShift
                ) {
                    return eventType?.name;
                }

                if (!event.efficiency) {
                    return eventType?.name;
                }

                return `${eventType?.name} - ${event.efficiency}%`;
            },
            getDurationEvent(event) {
                return event.allDay
                    ? this.$t("CalendarsPage.AllDay")
                    : `${event.startTime.slice(0, 5)} - ${event.endTime.slice(0, 5)}`;
            },
            handleDesktopOptionList(isRecurrent) {
                if (isRecurrent) return this.optionList;

                return this.optionList.filter((option) => option.id != 2);
            },
            handleMobileOptionList(isRecurrent) {
                if (isRecurrent) return this.mobileOptionList;

                return this.mobileOptionList.filter((option) => option.id != 3);
            },
            handleEditEvent(eventId) {
                this.UPDATE_MODAL_EVENT_ID(eventId);
                this.UPDATE_MODAL_EVENTS_TOGGLE(true);
            },
            getEvent(event) {
                this.currentEvent = event;
            },
            deleteEvent(eventId) {
                this.$swal({
                    title: this.$t("IntegrationPage.attention"),
                    html: `<div>${this.$t("CalendarsPage.DeleteEventMessage1")} <b>${this.currentEvent.name}</b>. ${this.$t("CalendarsPage.DeleteEventMessage2")}</div>`,
                    showCancelButton: true,
                    showCloseButton: true,
                    cancelButtonText: this.$t("IntegrationPage.cancel"),
                    confirmButtonText: this.$t("IntegrationPage.delete"),
                    customClass: {
                        container: "swal-default",
                        confirmButton: "btn btn-primary",
                        cancelButton: "btn btn-outline-danger ml-1"
                    },
                    buttonsStyling: false
                }).then((result) => {
                    if (result.value) {
                        this.callDeleteRoute(eventId);
                    }
                });
            },
            async callDeleteRoute(eventId) {
                this.UPDATE_FLAG_SHOW_OVERLAY(true);
                await this.$http2
                    .delete(`/api/myconfig/exception-calendar/${eventId}`)
                    .then(() => {
                        this.$setToast({
                            title: this.$t("Success"),
                            icon: "SmileIcon",
                            text: this.$t("CalendarsPage.EventDeleted"),
                            variant: "success"
                        });
                        this.$emit("updateEventList");
                    })
                    .catch((error) => {
                        error.response.data.errors.map((e) => {
                            this.$setToast({
                                title: this.$t("IntegrationPage.Error"),
                                icon: "SmileIcon",
                                text: e.message,
                                variant: "danger"
                            });
                        });
                    })
                    .finally(() => {
                        this.UPDATE_FLAG_SHOW_OVERLAY(false);
                    });
            },
            deleteRecurrenceEvent(event) {
                const hasDatePassed = this.verifyIfOutDated(
                    event.date,
                    event.startTime,
                    event.endTime,
                    event.allDay
                );

                this.$swal({
                    title: this.$t("IntegrationPage.attention"),
                    html: `<div>${this.$t("CalendarsPage.DeleteRecurrenceMessage1")} <b>${event.name}</b> ${hasDatePassed ? this.$t("CalendarsPage.DeleteRecurrenceMessage2") : this.$t("CalendarsPage.DeleteRecurrenceMessage3")}</div>`,
                    showCancelButton: true,
                    showCloseButton: true,
                    cancelButtonText: this.$t("IntegrationPage.cancel"),
                    confirmButtonText: this.$t("CalendarsPage.DeleteRecurrence"),
                    customClass: {
                        container: "swal-default",
                        confirmButton: "btn btn-primary",
                        cancelButton: "btn btn-outline-danger ml-1"
                    },
                    buttonsStyling: false
                }).then((result) => {
                    if (!result.value) return;

                    const monthsList = [...this.exception.monthGroups];
                    const eventList = monthsList.flatMap((monthGroup) => monthGroup.events);

                    if (!eventList?.length) return;

                    const principalEvent = eventList.find((e) => e.id == event.id);

                    const eventDate = new Date(event.date);
                    const startDate = new Date(event.recurrence.startDate);
                    const endDate = event.recurrence.endDate
                        ? new Date(event.recurrence.endDate)
                        : null;

                    const today = new Date();

                    today?.setHours?.(0, 0, 0, 0);
                    startDate?.setHours?.(0, 0, 0, 0);
                    endDate?.setHours?.(0, 0, 0, 0);
                    eventDate?.setHours?.(0, 0, 0, 0);

                    const isSameDate = startDate >= eventDate && startDate <= eventDate;

                    if (hasDatePassed || startDate >= today || isSameDate) {
                        principalEvent.recurrence = null;
                        principalEvent.isRecurrent = false;
                    } else if (
                        startDate < today &&
                        eventDate > today &&
                        ((endDate && endDate > today) || !endDate)
                    ) {
                        today.setDate(today.getDate() - 1);
                        const yesterday = today;
                        principalEvent.recurrence.endDate = yesterday.toISOString();
                    }

                    this.UPDATE_FLAG_SHOW_OVERLAY(true);
                    this.callEditEventRoute(principalEvent);
                });
            },
            async callEditEventRoute(event) {
                const payload = {
                    name: event.name,
                    description: event.description,
                    eventTypeId: event.eventTypeId,
                    efficiency: event.efficiency,
                    referenceDate: event.date,
                    allDay: event.allDay,
                    startTime: event.startTime,
                    endTime: event.endTime,
                    recurrent: event.isRecurrent,
                    resource: event.resourceIds,
                    recurrence: event.recurrence
                };
                await this.$http2
                    .put(`/api/myconfig/exception-calendar/${event.id}`, payload)
                    .then(() => {
                        this.$setToast({
                            title: this.$t("Success"),
                            icon: "SmileIcon",
                            text: this.$t("CalendarsPage.RecurrenceDeleted"),
                            variant: "success"
                        });

                        this.$emit("removeRecurrency");
                    })
                    .catch((error) => {
                        this.UPDATE_FLAG_SHOW_OVERLAY(false);

                        error.response.data.errors.map((e) => {
                            this.$setToast({
                                title: this.$t("IntegrationPage.Error"),
                                icon: "SmileIcon",
                                text: e.message,
                                variant: "danger"
                            });
                        });
                    });
            }
        },
        computed: {
            ...mapState("app", ["windowWidth"]),
            ...mapState("calendar", {
                exceptionPermissions: "exceptionPermissions",
                exception: "exception",
                context: "context"
            })
        }
    };
</script>

<style lang="scss" scoped>
    #events-block {
        padding-top: 8px;

        .event-block {
            display: flex;
            justify-content: space-between;
            gap: 16px;
            margin: 8px 0 0;
            border-radius: 6px;
            border: 1px solid #e0e0e0;
            background: #fff;
            padding: 15px 16px;

            &.--is-outdated {
                background: repeating-linear-gradient(
                    -45deg,
                    #eee,
                    #eee 25px,
                    #fff 25px,
                    #fff 40px
                );
            }
        }

        .day-block {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            text-align: center;
            padding-right: 16px;
            border-right: 1px solid #e0e0e0;
            max-width: 68px;
            width: 100%;

            .week-day {
                color: #998f8a;
                font-size: 12px;
                font-weight: 600;
                line-height: 16px;
            }

            .day {
                color: #4c4541;
                font-size: 22px;
                font-weight: 600;
                line-height: 30px;
            }
        }

        .event-info {
            width: 100%;
            min-width: 100px;

            .event-title {
                overflow: hidden;
                color: #4c4541;
                text-overflow: ellipsis;
                font-size: 16px;
                font-weight: 600;
                line-height: 26px;
                padding-bottom: 8px;
                white-space: nowrap;
            }

            .info-block {
                display: flex;
                gap: 10px;
                max-width: 100%;
                overflow: hidden;
                text-overflow: ellipsis;

                .first-column,
                .second-column {
                    display: flex;
                    flex-direction: column;
                    justify-content: space-between;
                    gap: 4px;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                }

                .first-column {
                    min-width: 150px;
                }

                .event-description,
                .event-duration,
                .event-recurrence,
                .event-resource {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;

                    .text {
                        color: #998f8a;
                        font-size: 12px;
                        font-weight: 600;
                        line-height: 16px;
                        text-overflow: ellipsis;
                        white-space: nowrap;
                        overflow: hidden;
                    }

                    .event-icon,
                    .duration-icon,
                    .recurrent-icon,
                    .resource-icon {
                        width: 12px;
                        height: 12px;
                        overflow: initial;
                        fill: #998f8a;
                    }
                }

                @media (max-width: 600px) {
                    flex-direction: column;
                    gap: 4px;

                    .first-column {
                        min-width: unset;
                    }
                }
            }

            @media (max-width: 480px) {
                min-width: 50px;
            }
        }

        .action-block {
            display: flex;
            align-items: center;
            gap: 8px;
        }

        .control-block {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: space-between;
            gap: 30px;
            width: 100%;
            max-width: 28px;

            .option-svg {
                fill: #974900;
                width: 28px;
                height: 28px;
                cursor: pointer;

                &:hover {
                    background-color: #ffede2;
                    border-radius: 5px;
                }

                &:active {
                    background-color: #ffdbc4;
                    border-radius: 5px;
                }

                &:focus-within,
                &:focus {
                    background-color: #ffdbc4;
                    border-radius: 5px;
                    border: none;
                    outline: none;
                }
            }

            .recurrent-icon {
                width: 12px;
                height: 12px;
            }
        }
    }
</style>
